/**
 * Address autocomplete on an existing <input>, built on the Places API (New)
 * (google.maps.places.AutocompleteSuggestion). Replaces the legacy
 * google.maps.places.Autocomplete, which is unavailable to Google Cloud
 * projects created after March 2025 (LegacyApiNotActivatedMapError).
 *
 * The callback receives the place in the legacy shape
 * ({ formatted_address, address_components: [{ long_name, short_name, types }] })
 * so existing "place_changed" handlers keep working unchanged.
 *
 * Requires "Places API (New)" to be enabled on the key's Cloud project.
 */

const DEBOUNCE_MS = 200;
const MIN_CHARS = 3;

const STYLE_ID = "places-autocomplete-style";
const CSS = `
.places-ac-list {
  position: fixed;
  z-index: 10000;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  font-size: 14px;
  max-height: 300px;
  overflow-y: auto;
}
.places-ac-item {
  padding: 8px 12px;
  cursor: pointer;
  color: #212121;
}
.places-ac-item.active,
.places-ac-item:hover {
  background: #f1f3f4;
}
.places-ac-attribution {
  padding: 4px 12px;
  font-size: 11px;
  color: #757575;
  text-align: right;
}
`;

function ensureStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

function toLegacyPlace(place) {
  return {
    formatted_address: place.formattedAddress || "",
    address_components: (place.addressComponents || []).map(c => ({
      long_name: c.longText,
      short_name: c.shortText,
      types: c.types || []
    }))
  };
}

/**
 * @param {HTMLInputElement|null} input
 * @param {(place: object) => void} onPlaceSelected
 * @param {{ selectFirstOnEnter?: boolean }} [options]
 * @returns {() => void} destroy function
 */
export function attachPlacesAutocomplete(input, onPlaceSelected, options = {}) {
  if (!input) return () => {};

  // Re-initialising the same input must not stack listeners/dropdowns.
  if (input.__placesAutocompleteDestroy) input.__placesAutocompleteDestroy();

  if (!window.google || !window.google.maps || !window.google.maps.importLibrary) {
    console.error("Google Maps JS API is not loaded; address autocomplete disabled.");
    return () => {};
  }

  ensureStyles();

  let destroyed = false;
  let places = null;
  let sessionToken = null;
  let suggestions = [];
  let activeIndex = -1;
  let debounceTimer = null;
  let requestId = 0;

  const list = document.createElement("div");
  list.className = "places-ac-list";
  list.style.display = "none";
  // Keep focus in the input while clicking the list.
  list.addEventListener("mousedown", e => e.preventDefault());
  document.body.appendChild(list);

  const position = () => {
    const rect = input.getBoundingClientRect();
    list.style.top = `${rect.bottom}px`;
    list.style.left = `${rect.left}px`;
    list.style.width = `${rect.width}px`;
  };

  const hide = () => {
    list.style.display = "none";
    activeIndex = -1;
  };

  const setActive = index => {
    activeIndex = index;
    Array.from(list.querySelectorAll(".places-ac-item")).forEach((el, i) => {
      el.classList.toggle("active", i === index);
    });
  };

  const select = async index => {
    const suggestion = suggestions[index];
    if (!suggestion) return;
    hide();
    try {
      const place = suggestion.placePrediction.toPlace();
      await place.fetchFields({
        fields: ["formattedAddress", "addressComponents"]
      });
      // The session ends once details are fetched; start a new one.
      sessionToken = new places.AutocompleteSessionToken();
      if (!destroyed) onPlaceSelected(toLegacyPlace(place));
    } catch (err) {
      console.error("Places autocomplete: failed to fetch place details", err);
    }
  };

  const render = () => {
    list.innerHTML = "";
    if (!suggestions.length) {
      hide();
      return;
    }
    suggestions.forEach((s, i) => {
      const item = document.createElement("div");
      item.className = "places-ac-item";
      item.textContent = s.placePrediction.text.text;
      item.addEventListener("mousedown", () => select(i));
      item.addEventListener("mouseenter", () => setActive(i));
      list.appendChild(item);
    });
    const attribution = document.createElement("div");
    attribution.className = "places-ac-attribution";
    attribution.textContent = "Powered by Google";
    list.appendChild(attribution);
    position();
    list.style.display = "block";
    activeIndex = -1;
  };

  const fetchSuggestions = async () => {
    const value = input.value;
    if (!places || !value || value.trim().length < MIN_CHARS) {
      suggestions = [];
      hide();
      return;
    }
    const current = ++requestId;
    try {
      const result = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
        { input: value, sessionToken }
      );
      if (destroyed || current !== requestId) return; // stale response
      suggestions = result.suggestions || [];
      render();
    } catch (err) {
      console.error("Places autocomplete: failed to fetch suggestions", err);
      suggestions = [];
      hide();
    }
  };

  const onInput = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(fetchSuggestions, DEBOUNCE_MS);
  };

  const onKeydown = e => {
    if (list.style.display === "none" || !suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((activeIndex + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((activeIndex - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      const index = activeIndex >= 0 ? activeIndex : options.selectFirstOnEnter ? 0 : -1;
      if (index < 0) return;
      e.preventDefault();
      select(index);
    } else if (e.key === "Escape") {
      hide();
    }
  };

  const onReposition = () => {
    if (list.style.display !== "none") position();
  };

  input.setAttribute("autocomplete", "off");
  input.addEventListener("input", onInput);
  input.addEventListener("keydown", onKeydown);
  input.addEventListener("blur", hide);
  window.addEventListener("resize", onReposition);
  window.addEventListener("scroll", onReposition, true);

  window.google.maps
    .importLibrary("places")
    .then(lib => {
      if (destroyed) return;
      places = lib;
      sessionToken = new lib.AutocompleteSessionToken();
    })
    .catch(err => console.error("Places autocomplete: failed to load library", err));

  const destroy = () => {
    destroyed = true;
    clearTimeout(debounceTimer);
    input.removeEventListener("input", onInput);
    input.removeEventListener("keydown", onKeydown);
    input.removeEventListener("blur", hide);
    window.removeEventListener("resize", onReposition);
    window.removeEventListener("scroll", onReposition, true);
    list.remove();
    delete input.__placesAutocompleteDestroy;
  };
  input.__placesAutocompleteDestroy = destroy;
  return destroy;
}
